const mem = new Map();

/**
 * cache key format: cache_[key]_[timestamp]
 * @param {string} key
 * @param {number} ts
 */
export function makeCacheStorageKey(key, ts = Date.now()) {
  return `cache_${key}_${ts}`;
}

/**
 * @template T
 * @param {string} key
 * @returns {T|null}
 */
export function cacheGet(key) {
  const entry = mem.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    mem.delete(key);
    return null;
  }
  return entry.value;
}

/**
 * @template T
 * @param {string} key
 * @param {T} value
 * @param {number} ttlMs
 * @returns {T}
 */
export function cacheSet(key, value, ttlMs) {
  const now = Date.now();
  mem.set(key, {
    storageKey: makeCacheStorageKey(key, now),
    value,
    createdAt: now,
    expiresAt: now + Math.max(0, ttlMs || 0),
  });
  return value;
}

/**
 * @template T
 * @param {string} key
 * @param {number} ttlMs
 * @param {() => Promise<T>} fetcher
 * @returns {Promise<T>}
 */
export async function cacheGetOrSet(key, ttlMs, fetcher) {
  const hit = cacheGet(key);
  if (hit != null) return hit;
  const next = await fetcher();
  return cacheSet(key, next, ttlMs);
}

export function cacheDelete(key) {
  mem.delete(key);
}

export function cacheClear() {
  mem.clear();
}

// TTL presets
export const CACHE_TTL = {
  FEATURE_FLAGS: 5 * 60 * 1000,
  ADS: 5 * 60 * 1000,
  REGION_LIST: 60 * 60 * 1000,
  CATEGORIES: 60 * 60 * 1000,
  /** In-memory only; same-tab revisits avoid duplicate Supabase reads */
  LISTINGS_BY_CATEGORY: 3 * 60 * 1000,
  WEATHER: 30 * 60 * 1000,
};

